import { jsPDF } from 'jspdf';
import JSZip from 'jszip';
import { usePrintEditorStore } from '../state/usePrintEditorStore';
import { getFrames } from '../schema/documentSchema';
import { renderDocumentToCanvas } from './renderDocumentToCanvas';

function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function dataUrlToBlob(dataUrl) {
    const [meta, b64] = dataUrl.split(',');
    const mime = /data:(.*?);base64/.exec(meta)?.[1] || 'application/octet-stream';
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
}

const PX_TO_MM = 25.4 / 96;

/**
 * One PDF, one page per rendered frame — each page sized to its own frame, so a
 * card front, its back and an A4 flyer can share a document without any of them
 * being letterboxed.
 *
 * `pages` = [{ pngDataUrl, width, height }] in the order they should print.
 */
function buildPdfFromPages(pages = []) {
    if (!pages.length) return null;
    const mm = (px) => Math.max(1, px * PX_TO_MM);
    const first = pages[0];
    const pdf = new jsPDF({
        orientation: mm(first.width) >= mm(first.height) ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [mm(first.width), mm(first.height)],
        compress: true,
    });
    pages.forEach((page, i) => {
        if (i > 0) {
            pdf.addPage(
                [mm(page.width), mm(page.height)],
                mm(page.width) >= mm(page.height) ? 'landscape' : 'portrait',
            );
        }
        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();
        // Tiny overscan kills hairline white edges from float rounding in PDF viewers
        const eps = 0.05;
        pdf.addImage(page.pngDataUrl, 'PNG', -eps, -eps, pageW + eps * 2, pageH + eps * 2, undefined, 'FAST');
    });
    return pdf.output('blob');
}

/** Filename-safe frame name, so "Card front" becomes "card-front". */
function slugify(name, fallback) {
    const slug = String(name || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    return slug || fallback;
}

function resolveDocument(doc) {
    return doc || usePrintEditorStore.getState().document;
}

/**
 * When the editor has a live selection, build render options that export ONLY
 * those elements — cropped to their combined bounds on a transparent backdrop.
 * Returns null when nothing visible is selected, so callers fall back to a
 * full-design export.
 */
export function selectionExportOptions() {
    const state = usePrintEditorStore.getState();
    const ids = state?.selectedIds || [];
    if (!ids.length) return null;
    const sel = new Set(ids);
    const visibleSelected = (state.document?.elements || [])
        .filter((el) => sel.has(el.id) && el.visible !== false)
        .map((el) => el.id);
    if (!visibleSelected.length) return null;
    return { only: visibleSelected, crop: true, backgroundColor: 'transparent' };
}

/**
 * Capture artboard by rendering document JSON → Canvas2D (no html2canvas).
 */
export async function captureArtboard({
    scale = 2,
    backgroundColor,
    document: doc,
    only,
    crop,
    frameId,
} = {}) {
    const { canvas, width, height } = await renderDocumentToCanvas(resolveDocument(doc), {
        scale,
        backgroundColor,
        only,
        crop,
        frameId,
    });
    return {
        canvas,
        width,
        height,
        cleanup: () => {},
    };
}

/**
 * Export the template design to a PNG (optionally download).
 */
export async function exportCanvasPng(options = {}) {
    const { canvas, cleanup } = await captureArtboard({
        scale: options.scale || 2,
        backgroundColor: options.backgroundColor,
        document: options.document,
        only: options.only,
        crop: options.crop,
    });
    try {
        const dataUrl = canvas.toDataURL('image/png');
        if (options.download) {
            downloadBlob(dataUrlToBlob(dataUrl), options.filename || 'print-template.png');
        }
        return dataUrl;
    } finally {
        cleanup();
    }
}

/**
 * Export PNG + PDF together as a single ZIP download.
 * Renders from the template document so zoom/selection cannot break the design.
 */
export async function exportCanvasZip(options = {}) {
    const filenameBase = (options.filenameBase || 'print-template').replace(/\.zip$/i, '');
    const doc = resolveDocument(options.document);
    const scale = options.scale || 2;

    // A selection export is one cropped image of exactly what is selected — frames
    // don't come into it.
    const frames = options.only ? [] : getFrames(doc);
    if (!options.only && !frames.length) {
        // Every frame deleted: there is nothing to render, and writing a blank page
        // would be worse than saying so.
        return {
            zipBlob: null, pngBlob: null, pdfBlob: null, pngDataUrl: null,
            width: 0, height: 0, pages: [],
        };
    }
    const targets = frames.length ? frames : [null];

    const pages = [];
    for (const frame of targets) {
        // Sequential on purpose: each render builds full-size offscreen canvases, and
        // firing every frame at once on a big document is how you hit memory limits.
        // eslint-disable-next-line no-await-in-loop
        const { canvas, width, height } = await renderDocumentToCanvas(doc, {
            scale,
            backgroundColor: options.backgroundColor,
            only: options.only,
            crop: options.crop,
            frameId: frame?.id,
        });
        pages.push({
            frame,
            width,
            height,
            pngDataUrl: canvas.toDataURL('image/png'),
        });
    }

    const zip = new JSZip();
    const multi = pages.length > 1;
    pages.forEach((page, i) => {
        // One frame → the filename callers have always got. Several → one file per
        // frame, named after it, numbered so the print order survives sorting.
        const name = multi
            ? `${filenameBase}-${String(i + 1).padStart(2, '0')}-${slugify(page.frame?.name, `frame-${i + 1}`)}`
            : filenameBase;
        zip.file(`${name}.png`, dataUrlToBlob(page.pngDataUrl));
    });
    const pdfBlob = buildPdfFromPages(pages);
    if (pdfBlob) zip.file(`${filenameBase}.pdf`, pdfBlob);
    const zipBlob = await zip.generateAsync({ type: 'blob' });

    if (options.download !== false) {
        downloadBlob(zipBlob, `${filenameBase}.zip`);
    }

    // The first page's PNG is returned as `pngBlob`/`pngDataUrl` — that is what
    // callers persist as the saved artwork/thumbnail, and it stays the front of the
    // design however many frames the template has. `pages` carries the rest.
    const first = pages[0];
    return {
        zipBlob,
        pngBlob: first ? dataUrlToBlob(first.pngDataUrl) : null,
        pdfBlob,
        pngDataUrl: first?.pngDataUrl || null,
        width: first?.width || 0,
        height: first?.height || 0,
        pages: pages.map((p) => ({
            frameId: p.frame?.id || null,
            name: p.frame?.name || null,
            width: p.width,
            height: p.height,
            pngBlob: dataUrlToBlob(p.pngDataUrl),
            pngDataUrl: p.pngDataUrl,
        })),
    };
}

/**
 * Small JPEG preview for gallery cards.
 *
 * `frameId` picks which artboard is captured — a multi-frame template's card can
 * show its back, or an inside page, instead of always frame 1. Omitted → frame 1.
 */
export async function exportCanvasThumbnail({
    maxWidth = 480, quality = 0.72, document: doc, frameId,
} = {}) {
    try {
        const { canvas: source, cleanup } = await captureArtboard({
            scale: 1,
            document: doc,
            frameId,
        });
        try {
            const ratio = Math.min(1, maxWidth / source.width);
            const w = Math.max(1, Math.round(source.width * ratio));
            const h = Math.max(1, Math.round(source.height * ratio));
            const out = document.createElement('canvas');
            out.width = w;
            out.height = h;
            const ctx = out.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(source, 0, 0, w, h);
            return out.toDataURL('image/jpeg', quality);
        } finally {
            cleanup();
        }
    } catch {
        return null;
    }
}

export function applyPlaceholderValues(document, values = {}) {
    const elements = (document.elements || []).map((el) => {
        if (el.type === 'placeholder' && el.placeholderKey && values[el.placeholderKey] != null) {
            return { ...el, content: String(values[el.placeholderKey]) };
        }
        if ((el.type === 'qrcode' || el.type === 'barcode') && el.placeholderKey && values[el.placeholderKey] != null) {
            return { ...el, content: String(values[el.placeholderKey]) };
        }
        return el;
    });
    return { ...document, elements };
}
